// Central, reusable texture library. Any component can import TEXTURES,
// getTexture(id), getTextureUrl(id) or <TexturePicker/> to offer these fills.
// Grouped by category for nicer pickers.

export const TEXTURES = [
  // ── Fluid & Marble ──────────────────────────────────────────────
  { id: "purple-liquid", label: "Purple Liquid", category: "Fluid & Marble", text: "light", url: "https://images.pexels.com/photos/33360833/pexels-photo-33360833.jpeg?auto=compress&cs=tinysrgb&w=1200" },
  { id: "rainbow-pour", label: "Rainbow Pour", category: "Fluid & Marble", text: "dark", url: "https://images.unsplash.com/photo-1614519679749-3189ec5687d9?auto=format&fit=crop&w=1200&q=80" },
  { id: "teal-mist", label: "Teal Mist", category: "Fluid & Marble", text: "dark", url: "https://images.unsplash.com/photo-1603277578692-c699f37c67d3?auto=format&fit=crop&w=1200&q=80" },

  // ── Cosmic ──────────────────────────────────────────────────────
  { id: "purple-nebula", label: "Purple Nebula", category: "Cosmic", text: "light", url: "https://images.unsplash.com/photo-1610209455607-89e8b3e0e393?auto=format&fit=crop&w=1200&q=80" },
  { id: "galaxy-core", label: "Galaxy Core", category: "Cosmic", text: "light", url: "https://images.pexels.com/photos/23522825/pexels-photo-23522825.png?auto=compress&cs=tinysrgb&w=1200" },
  { id: "cosmic-burst", label: "Cosmic Burst", category: "Cosmic", text: "light", url: "https://images.pexels.com/photos/20083457/pexels-photo-20083457.jpeg?auto=compress&cs=tinysrgb&w=1200" },

  // ── Nature ──────────────────────────────────────────────────────
  { id: "tropical-leaves", label: "Tropical Leaves", category: "Nature", text: "light", url: "https://images.unsplash.com/photo-1503149779833-1de50ebe5f8a?auto=format&fit=crop&w=1200&q=80" },
  { id: "croton-foliage", label: "Croton Foliage", category: "Nature", text: "light", url: "https://images.unsplash.com/photo-1774149970501-0e5b3874c3c8?auto=format&fit=crop&w=1200&q=80" },

  // ── Fire & Energy ───────────────────────────────────────────────
  { id: "fire", label: "Fire", category: "Fire & Energy", text: "light", url: "https://images.unsplash.com/photo-1507048947301-7afc2aca0edc?auto=format&fit=crop&w=1200&q=80" },
  { id: "light-waves", label: "Light Waves", category: "Fire & Energy", text: "light", url: "https://images.pexels.com/photos/16503683/pexels-photo-16503683.jpeg?auto=compress&cs=tinysrgb&w=1200" },

  // ── Material ────────────────────────────────────────────────────
  { id: "crystal-glitter", label: "Crystal Glitter", category: "Material", text: "dark", url: "https://images.unsplash.com/photo-1616000315178-317bbc76ea71?auto=format&fit=crop&w=1200&q=80" },
  { id: "denim", label: "Denim", category: "Material", text: "light", url: "https://images.unsplash.com/photo-1645859610425-f0f4177df5f0?auto=format&fit=crop&w=1200&q=80" },
  { id: "cream-plaster", label: "Cream Plaster", category: "Material", text: "dark", url: "https://images.unsplash.com/photo-1602454988916-e05e2cb47373?auto=format&fit=crop&w=1200&q=80" },
];

// Readable text presets per background brightness.
export const TEXT_MODES = {
  light: { color: "#ffffff", shadow: "0 1px 4px rgba(0,0,0,0.6)" },
  dark: { color: "#18181b", shadow: "0 1px 3px rgba(255,255,255,0.55)" },
};

export const TEXTURE_CATEGORIES = [...new Set(TEXTURES.map((t) => t.category))];

export const getTexture = (id) => TEXTURES.find((t) => t.id === id) || null;

export const getTextureUrl = (id) => getTexture(id)?.url || "";

export const getTextureTextMode = (id) => TEXT_MODES[getTexture(id)?.text] || null;

// Returns a card-ready texture descriptor { url, mode, color, shadow } or null.
export function getTextureCard(id) {
  const t = getTexture(id);
  if (!t) return null;
  const mode = TEXT_MODES[t.text] || TEXT_MODES.dark;
  return { url: t.url, mode: t.text, color: mode.color, shadow: mode.shadow };
}

// Resolve a highlight/graphic config that may use a texture into a plain
// image graphic (so consumers/components stay decoupled from the registry).
// Also surfaces an auto-picked, readable text color + shadow for the texture.
export function resolveGraphic(graphic = {}) {
  if (graphic.variant === "texture") {
    const mode = getTextureTextMode(graphic.texture_id);
    return {
      ...graphic,
      variant: "image",
      image_url: getTextureUrl(graphic.texture_id) || graphic.image_url || "",
      autoTextColor: mode?.color,
      autoTextShadow: mode?.shadow,
    };
  }
  return graphic;
}
